import type { RootStore } from 'logic';
import { computed, makeObservable, reaction } from 'mobx';
import { RouteNames } from './routes';

// Session surfaces we can safely return to on exiting a card (sessionId-only
// params). Anything else falls back to the queue, preserving prior behavior.
const SAFE_RETURN_ROUTES = new Set<RouteNames>([
  RouteNames.SESSION_WHOLE,
  RouteNames.SESSION_QUEUE,
  RouteNames.SESSION_SEARCH,
  RouteNames.SESSION_LIBRARY,
  RouteNames.SESSION_ASSETS,
  RouteNames.SESSION_HOME,
]);

export class UiCardView {
  private rootStore: RootStore;
  // The route a card was opened FROM, so "back" returns there (WHOLE → WHOLE,
  // CARDS → CARDS) instead of always dumping you on the queue.
  private originRouteName: RouteNames | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      selectedCardId: computed,
      isCardFocused: computed,
    });
  }

  init() {
    reaction(
      () => this.selectedCardId,
      () => {
        if (this.selectedCardId) {
          this.rootStore.uiActiveSessionStore.toggleSelectedCard(this.selectedCardId);
        }
      },
      { fireImmediately: true },
    );
    reaction(
      // Ensure that you can never deselect the focused card
      () => this.rootStore.uiActiveSessionStore.selectedCardIds,
      (selectedIds) => {
        if (this.isCardFocused && !selectedIds.includes(this.selectedCardId!)) {
          this.rootStore.uiActiveSessionStore.selectCardIds([this.selectedCardId!]);
        }
      },
    );
  }

  get selectedCardId() {
    const { routerState } = this.rootStore.routerStore;
    const cardId = routerState?.params?.cardId;
    return typeof cardId === 'string' ? cardId : undefined;
  }

  get isCardFocused() {
    return !!this.selectedCardId;
  }

  exitCardFocus = () => {
    if (!this.selectedCardId) return;
    this.toggleCardFocus(this.selectedCardId);
  };

  toggleCardFocus = (cardId: string) => {
    if (cardId === this.selectedCardId) {
      // Exit: return to the surface the card was opened from (WHOLE / CARDS /
      // SEARCH …). Fall back to the queue for deep links or complex routes.
      const target =
        this.originRouteName && SAFE_RETURN_ROUTES.has(this.originRouteName)
          ? this.originRouteName
          : RouteNames.SESSION_QUEUE;
      this.originRouteName = null;
      this.rootStore.routerStore.goTo(target, {
        params: { sessionId: this.rootStore.sessionsStore.currentSessionId },
      });
      this.rootStore.uiActiveSessionStore.deselectAllCards();
    } else {
      // Enter: remember where we came from (ignore card→card hops so the origin
      // stays the original surface).
      const current = this.rootStore.routerStore.routerState?.routeName as RouteNames | undefined;
      if (current && current !== RouteNames.CARD) {
        this.originRouteName = current;
      }
      this.rootStore.uiActiveSessionStore.deselectAllCards();
      this.rootStore.uiActiveSessionStore.toggleSelectedCard(cardId);
      this.rootStore.routerStore.goTo(RouteNames.CARD, {
        params: {
          sessionId: this.rootStore.sessionsStore.currentSessionId,
          cardId,
        },
      });
    }
  };
}
